/**
 * Multi-track timeline math for Movie Studio (VideoProject v2).
 *
 * Clips have independent start times. A transition plays where two clips on
 * the same track overlap, or across a cut when they only meet. Media bytes
 * never live on the clip — only the refs already used by video and audio.
 */

import type {
  AudioTrack,
  ProjectPage,
  SceneTransition,
  TimelineClip,
  TimelineTrack,
  VideoBrand,
  VideoCanvas,
  VideoProject,
  VideoScene,
} from '@canvas/shared-types';
import {
  VIDEO_MAX_CLIPS,
  VIDEO_MAX_TOTAL_MS,
  VIDEO_MAX_TRANSITION_MS,
  VIDEO_MIN_CLIP_MS,
} from '@canvas/shared-types';
import { DEFAULT_BRAND_KIT } from '@/data/brandKit';
import { buildCues, createVideoProject, withDerived } from './sceneTimeline';

export const TRACK = {
  textA: 'text-a',
  textB: 'text-b',
  visual: 'visual-a',
  visualB: 'visual-b',
  visualC: 'visual-c',
  caption: 'caption-a',
  audioA: 'audio-a',
  audioB: 'audio-b',
  audioC: 'audio-c',
  audioD: 'audio-d',
} as const;

/** Snap distance at the current zoom, matching a Clipchamp-style 8px magnet. */
export const SNAP_PX = 8;

let idSeq = 0;

/** Stable-enough id for clips created in the editor. Tests can reset the counter. */
export function nextClipId(prefix = 'clip'): string {
  idSeq += 1;
  return `${prefix}_${idSeq.toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function resetClipIds(start = 0): void {
  idSeq = start;
}

export function defaultVideoBrand(): VideoBrand {
  return {
    name: DEFAULT_BRAND_KIT.name,
    colors: DEFAULT_BRAND_KIT.colors.map((color) => ({
      id: color.id,
      name: color.name,
      hex: color.hex,
    })),
    fonts: DEFAULT_BRAND_KIT.fonts.map((font) => ({
      id: font.id,
      name: font.name,
      family: font.family,
    })),
    logoUrl: '/brand/icon.png',
  };
}

export function defaultTracks(): TimelineTrack[] {
  return [
    { id: TRACK.textA, kind: 'text', name: 'Text 1', index: 8 },
    { id: TRACK.textB, kind: 'text', name: 'Text 2', index: 7 },
    { id: TRACK.caption, kind: 'caption', name: 'Captions', index: 6 },
    { id: TRACK.visualC, kind: 'visual', name: 'Video 3', index: 3 },
    { id: TRACK.visualB, kind: 'visual', name: 'Video 2', index: 2 },
    { id: TRACK.visual, kind: 'visual', name: 'Video 1', index: 1 },
    { id: TRACK.audioA, kind: 'audio', name: 'Audio 1', index: 3 },
    { id: TRACK.audioB, kind: 'audio', name: 'Audio 2', index: 2 },
    { id: TRACK.audioC, kind: 'audio', name: 'Audio 3', index: 1 },
    { id: TRACK.audioD, kind: 'audio', name: 'Audio 4', index: 0 },
  ];
}

/** Keep a drop on a lane of the same kind. Anything else stays on the first lane of that kind. */
export function trackForKind(video: VideoProject, trackId: string | undefined, kind: TimelineTrack['kind']): string {
  const track = trackId ? video.tracks?.find((item) => item.id === trackId) : undefined;
  if (track?.kind === kind) return track.id;
  return defaultTracks().find((item) => item.kind === kind)?.id ?? TRACK.visual;
}

export function isClipProject(video: VideoProject | null | undefined): video is VideoProject & {
  v: 2;
  canvas: VideoCanvas;
  tracks: TimelineTrack[];
  clips: TimelineClip[];
  brand: VideoBrand;
} {
  return Boolean(
    video &&
      video.v === 2 &&
      video.canvas &&
      video.tracks &&
      video.clips &&
      video.brand
  );
}

export function clipEnd(clip: TimelineClip): number {
  return clip.startMs + clip.durationMs;
}

export function timelineSpan(clips: readonly TimelineClip[]): number {
  return clips.reduce((max, clip) => Math.max(max, clipEnd(clip)), 0);
}

/** Empty Movie Studio timeline: text, captions, three video lanes, four audio lanes. */
export function createClipProject(
  artboard: { width: number; height: number },
  now: number
): VideoProject {
  const base = createVideoProject([], artboard, now);
  return withDerived({
    ...base,
    v: 2,
    scenes: [],
    audio: null,
    canvas: { width: artboard.width, height: artboard.height, bg: '#111111' },
    tracks: defaultTracks(),
    clips: [],
    brand: defaultVideoBrand(),
  });
}

/**
 * Turn a v1 scene lane into clips. Scene order becomes visual clips laid end
 * to end (transition overlap included). The single music track becomes an
 * audio clip. Already-v2 documents pass through.
 */
export function migrateVideoProject(
  video: VideoProject,
  artboard: { width: number; height: number }
): VideoProject {
  if (isClipProject(video)) return ensureTracks(video);
  const tracks = defaultTracks();
  const cues = buildCues(video.scenes);
  const clips: TimelineClip[] = cues.map((cue, index) => {
    const scene: VideoScene | undefined = video.scenes[index];
    return {
      id: `scene_${cue.pageId}`,
      trackId: TRACK.visual,
      startMs: cue.startMs,
      durationMs: Math.max(VIDEO_MIN_CLIP_MS, cue.endMs - cue.startMs),
      name: scene?.note || `Scene ${index + 1}`,
      kind: 'scene',
      scene: { pageId: cue.pageId },
      transitionIn: index > 0 ? scene?.transitionIn : undefined,
    };
  });
  if (video.audio) {
    const audio = video.audio;
    const durationMs = Math.max(VIDEO_MIN_CLIP_MS, audio.trimEndMs - audio.trimStartMs);
    clips.push({
      id: `audio_${audio.trackId}`,
      trackId: TRACK.audioA,
      startMs: audio.offsetMs,
      durationMs,
      name: audio.name,
      kind: 'audio',
      audio: {
        v: 1,
        source: audio.source,
        storagePath: audio.storagePath,
        url: audio.url,
        idbKey: audio.idbKey,
        mimeType: audio.mimeType,
        durationMs: audio.durationMs,
        trimStartMs: audio.trimStartMs,
        trimEndMs: audio.trimEndMs,
        volume: audio.volume,
        fade: audio.fade,
      },
    });
  }
  return withDerived({
    ...video,
    v: 2,
    canvas: video.canvas ?? { width: artboard.width, height: artboard.height, bg: '#111111' },
    tracks,
    clips,
    brand: video.brand ?? defaultVideoBrand(),
  });
}

function commit(video: VideoProject, clips: TimelineClip[], tracks?: TimelineTrack[]): VideoProject {
  if (!isClipProject(video)) return video;
  return withDerived({
    ...video,
    clips,
    tracks: tracks ?? video.tracks,
  });
}

/** Add any missing default lanes and keep overlay video above the main picture. */
export function ensureTracks(video: VideoProject): VideoProject {
  if (!isClipProject(video)) return video;
  const defaults = defaultTracks();
  const byId = new Map(defaults.map((track) => [track.id, track]));
  let changed = false;
  const tracks = video.tracks.map((track) => {
    const fresh = byId.get(track.id);
    if (!fresh || (fresh.name === track.name && fresh.index === track.index && fresh.kind === track.kind)) return track;
    changed = true;
    return { ...track, name: fresh.name, index: fresh.index, kind: fresh.kind };
  });
  for (const fresh of defaults) {
    if (tracks.some((track) => track.id === fresh.id)) continue;
    tracks.push(fresh);
    changed = true;
  }
  if (!changed) return video;
  return commit(video, video.clips, tracks);
}

function trackOf(video: VideoProject, trackId: string): TimelineTrack | undefined {
  return video.tracks?.find((track) => track.id === trackId);
}

/** Guides a drag can snap to: playhead and every other clip edge. */
export function snapTargets(clips: readonly TimelineClip[], exceptIds: ReadonlySet<string>, playheadMs: number): number[] {
  const guides = [0, playheadMs];
  for (const clip of clips) {
    if (exceptIds.has(clip.id)) continue;
    guides.push(clip.startMs, clipEnd(clip));
  }
  return guides;
}

export function snapTime(ms: number, guides: readonly number[], pxPerSec: number): number {
  const thresholdMs = pxPerSec > 0 ? (SNAP_PX / pxPerSec) * 1000 : 0;
  let best = ms;
  let bestDist = thresholdMs;
  for (const guide of guides) {
    const dist = Math.abs(guide - ms);
    if (dist <= bestDist) {
      best = guide;
      bestDist = dist;
    }
  }
  return Math.round(best);
}

function clampPlacement(startMs: number, durationMs: number): { startMs: number; durationMs: number } {
  const duration = Math.max(VIDEO_MIN_CLIP_MS, Math.round(durationMs));
  const start = Math.max(0, Math.round(startMs));
  const end = Math.min(VIDEO_MAX_TOTAL_MS, start + duration);
  return { startMs: start, durationMs: Math.max(VIDEO_MIN_CLIP_MS, end - start) };
}

/** Visual clips showing at `ms`, bottom to top. */
export function clipsAt(video: VideoProject, ms: number): TimelineClip[] {
  if (!isClipProject(video)) return [];
  const order = new Map(video.tracks.map((track) => [track.id, track.index]));
  const hidden = new Set(video.tracks.filter((track) => track.hidden).map((track) => track.id));
  return video.clips
    .filter(
      (clip) =>
        !hidden.has(clip.trackId) &&
        clip.kind !== 'audio' &&
        ms >= clip.startMs &&
        ms < clipEnd(clip)
    )
    .sort((a, b) => (order.get(a.trackId) ?? 0) - (order.get(b.trackId) ?? 0) || a.startMs - b.startMs);
}

export interface ClipBlend {
  outgoing: TimelineClip;
  incoming: TimelineClip;
  /** 0 = outgoing, 1 = incoming. */
  progress: number;
}

function blendWindow(a: TimelineClip, b: TimelineClip): { start: number; end: number } | null {
  const transition = b.transitionIn;
  if (!transition || transition.id === 'none' || transition.id === 'cut') return null;
  const dur = Math.min(
    transition.durationMs || 0,
    VIDEO_MAX_TRANSITION_MS,
    a.durationMs / 2,
    b.durationMs / 2
  );
  if (dur <= 0) return null;
  const overlapStart = Math.max(a.startMs, b.startMs);
  const overlapEnd = Math.min(clipEnd(a), clipEnd(b));
  if (overlapEnd > overlapStart) {
    return { start: overlapStart, end: Math.min(overlapEnd, overlapStart + dur) };
  }
  return { start: b.startMs - dur, end: b.startMs };
}

/** Active transitions, one per track at most. */
export function clipBlendsAt(clips: readonly TimelineClip[], ms: number): ClipBlend[] {
  const byTrack = new Map<string, TimelineClip[]>();
  for (const clip of clips) {
    if (clip.kind === 'audio') continue;
    const list = byTrack.get(clip.trackId) ?? [];
    list.push(clip);
    byTrack.set(clip.trackId, list);
  }
  const blends: ClipBlend[] = [];
  for (const list of byTrack.values()) {
    list.sort((a, b) => a.startMs - b.startMs);
    for (let i = 1; i < list.length; i += 1) {
      const window = blendWindow(list[i - 1], list[i]);
      if (!window || ms < window.start || ms >= window.end || window.end <= window.start) continue;
      blends.push({
        outgoing: list[i - 1],
        incoming: list[i],
        progress: (ms - window.start) / (window.end - window.start),
      });
    }
  }
  return blends;
}

export function insertClip(video: VideoProject, clip: TimelineClip): VideoProject {
  if (!isClipProject(video)) return video;
  if (video.clips.length >= VIDEO_MAX_CLIPS) return video;
  if (!trackOf(video, clip.trackId) || trackOf(video, clip.trackId)?.locked) return video;
  const placed = clampPlacement(clip.startMs, clip.durationMs);
  return commit(video, [...video.clips, { ...clip, ...placed }]);
}

export function insertClips(video: VideoProject, clips: TimelineClip[]): VideoProject {
  return clips.reduce((next, clip) => insertClip(next, clip), video);
}

function groupIds(clips: readonly TimelineClip[], id: string): Set<string> {
  const clip = clips.find((item) => item.id === id);
  if (!clip?.groupId) return new Set([id]);
  return new Set(clips.filter((item) => item.groupId === clip.groupId).map((item) => item.id));
}

export function moveClip(
  video: VideoProject,
  id: string,
  startMs: number,
  trackId?: string,
  pxPerSec = 80,
  playheadMs = 0
): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip) return video;
  const moving = groupIds(video.clips, id);
  const nextTrack = trackId && !clip.groupId ? trackId : clip.trackId;
  if (trackOf(video, nextTrack)?.locked || trackOf(video, clip.trackId)?.locked) return video;
  const guides = snapTargets(video.clips, moving, playheadMs);
  const snapped = snapTime(startMs, guides, pxPerSec);
  const delta = snapped - clip.startMs;
  const clips = video.clips.map((item) => {
    if (!moving.has(item.id)) return item;
    const track = item.id === id ? nextTrack : item.trackId;
    const placed = clampPlacement(item.startMs + delta, item.durationMs);
    return { ...item, trackId: track, startMs: placed.startMs, durationMs: placed.durationMs };
  });
  return commit(video, clips);
}

function shiftTrim(clip: TimelineClip, deltaMs: number, edge: 'start' | 'end'): TimelineClip {
  if (edge === 'start' && clip.video) {
    return {
      ...clip,
      video: {
        ...clip.video,
        trimStartMs: Math.min(clip.video.trimEndMs - VIDEO_MIN_CLIP_MS, clip.video.trimStartMs + deltaMs),
      },
    };
  }
  if (edge === 'end' && clip.video) {
    return {
      ...clip,
      video: {
        ...clip.video,
        trimEndMs: Math.max(clip.video.trimStartMs + VIDEO_MIN_CLIP_MS, clip.video.trimEndMs + deltaMs),
      },
    };
  }
  if (edge === 'start' && clip.audio) {
    return {
      ...clip,
      audio: {
        ...clip.audio,
        trimStartMs: Math.min(clip.audio.trimEndMs - VIDEO_MIN_CLIP_MS, clip.audio.trimStartMs + deltaMs),
      },
    };
  }
  if (edge === 'end' && clip.audio) {
    return {
      ...clip,
      audio: {
        ...clip.audio,
        trimEndMs: Math.max(clip.audio.trimStartMs + VIDEO_MIN_CLIP_MS, clip.audio.trimEndMs + deltaMs),
      },
    };
  }
  return clip;
}

export function trimClip(
  video: VideoProject,
  id: string,
  edge: 'start' | 'end',
  timeMs: number,
  pxPerSec = 80,
  playheadMs = 0
): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip || trackOf(video, clip.trackId)?.locked) return video;
  const guides = snapTargets(video.clips, new Set([id]), playheadMs);
  const snapped = snapTime(timeMs, guides, pxPerSec);
  let next = clip;
  if (edge === 'start') {
    const end = clipEnd(clip);
    const start = Math.min(snapped, end - VIDEO_MIN_CLIP_MS);
    const delta = Math.max(0, start) - clip.startMs;
    const placed = clampPlacement(Math.max(0, start), end - Math.max(0, start));
    next = shiftTrim({ ...clip, ...placed }, delta, 'start');
  } else {
    const end = Math.max(clip.startMs + VIDEO_MIN_CLIP_MS, snapped);
    const delta = end - clipEnd(clip);
    const placed = clampPlacement(clip.startMs, end - clip.startMs);
    next = shiftTrim({ ...clip, ...placed }, delta, 'end');
  }
  return commit(
    video,
    video.clips.map((item) => (item.id === id ? next : item))
  );
}

export function splitClip(video: VideoProject, id: string, atMs: number): VideoProject {
  if (!isClipProject(video)) return video;
  if (video.clips.length >= VIDEO_MAX_CLIPS) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip) return video;
  const local = atMs - clip.startMs;
  if (local < VIDEO_MIN_CLIP_MS || clip.durationMs - local < VIDEO_MIN_CLIP_MS) return video;
  const left = shiftTrim({ ...clip, durationMs: local, groupId: undefined }, 0, 'end');
  const rightBase: TimelineClip = {
    ...clip,
    id: nextClipId('split'),
    startMs: atMs,
    durationMs: clip.durationMs - local,
    groupId: undefined,
    transitionIn: undefined,
  };
  const right = shiftTrim(rightBase, local, 'start');
  return commit(
    video,
    video.clips.flatMap((item) => (item.id === id ? [left, right] : [item]))
  );
}

export function duplicateClip(video: VideoProject, id: string): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip) return video;
  return insertClip(video, {
    ...clip,
    id: nextClipId('copy'),
    startMs: clipEnd(clip),
    groupId: undefined,
    name: `${clip.name} copy`,
  });
}

export function renameClip(video: VideoProject, id: string, name: string): VideoProject {
  if (!isClipProject(video)) return video;
  const nextName = name.trim().slice(0, 80);
  if (!nextName) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip || clip.name === nextName || trackOf(video, clip.trackId)?.locked) return video;
  return commit(
    video,
    video.clips.map((item) => (item.id === id ? { ...item, name: nextName } : item))
  );
}

/** Typed duration. Snapping is for drags; a typed value lands on the number entered. */
export function setClipDuration(video: VideoProject, id: string, durationMs: number): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip || trackOf(video, clip.trackId)?.locked) return video;
  const placed = clampPlacement(clip.startMs, durationMs);
  if (placed.durationMs === clip.durationMs && placed.startMs === clip.startMs) return video;
  const delta = placed.durationMs - clip.durationMs;
  const next = shiftTrim({ ...clip, ...placed }, delta, 'end');
  return commit(
    video,
    video.clips.map((item) => (item.id === id ? next : item))
  );
}

/** Insert a clipboard snapshot at `atMs` on its own track. */
export function pasteClip(video: VideoProject, source: TimelineClip, atMs: number): VideoProject {
  return insertClip(video, {
    ...source,
    id: nextClipId('paste'),
    startMs: Math.max(0, atMs),
    groupId: undefined,
    transitionIn: undefined,
  });
}

export function removeClip(video: VideoProject, id: string): VideoProject {
  if (!isClipProject(video)) return video;
  const clips = video.clips.filter((clip) => clip.id !== id);
  if (clips.length === video.clips.length) return video;
  return commit(video, clips);
}

export interface TrackGap {
  trackId: string;
  startMs: number;
  endMs: number;
}

/** Empty time on a track that has a clip after it. Trailing space is not a gap. */
export function gapAt(clips: readonly TimelineClip[], trackId: string, ms: number): TrackGap | null {
  const onTrack = clips
    .filter((clip) => clip.trackId === trackId)
    .slice()
    .sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
  let cursor = 0;
  for (const clip of onTrack) {
    if (ms >= clip.startMs && ms < clipEnd(clip)) return null;
    if (ms >= cursor && ms < clip.startMs && clip.startMs - cursor >= 40) {
      return { trackId, startMs: cursor, endMs: clip.startMs };
    }
    cursor = Math.max(cursor, clipEnd(clip));
  }
  return null;
}

/**
 * Pull later clips left so the empty stretch under `ms` disappears.
 * A partner that starts with a moved clip, such as a story caption, moves too.
 */
export function closeGap(video: VideoProject, trackId: string, ms: number): VideoProject {
  if (!isClipProject(video)) return video;
  if (trackOf(video, trackId)?.locked) return video;
  const gap = gapAt(video.clips, trackId, ms);
  if (!gap) return video;
  const delta = gap.startMs - gap.endMs;
  const moving = new Set(
    video.clips.filter((clip) => clip.trackId === trackId && clip.startMs >= gap.endMs - 1).map((clip) => clip.id)
  );
  if (!moving.size) return video;
  for (const clip of video.clips) {
    if (moving.has(clip.id) || !clip.groupId) continue;
    const partner = video.clips.find(
      (other) => moving.has(other.id) && other.groupId === clip.groupId && Math.abs(other.startMs - clip.startMs) < 50
    );
    if (partner) moving.add(clip.id);
  }
  return commit(
    video,
    video.clips.map((clip) => (moving.has(clip.id) ? { ...clip, startMs: Math.max(0, clip.startMs + delta) } : clip))
  );
}

export function ungroupClip(video: VideoProject, id: string): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip?.groupId) return video;
  const groupId = clip.groupId;
  return commit(
    video,
    video.clips.map((item) => (item.groupId === groupId ? { ...item, groupId: undefined } : item))
  );
}

export function restackClip(video: VideoProject, id: string, direction: 'forward' | 'backward'): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip || clip.kind === 'audio') return video;
  const tracks = video.tracks
    .filter((track) => track.kind !== 'audio' && !track.hidden && !track.locked)
    .slice()
    .sort((a, b) => a.index - b.index);
  const trackIndex = tracks.findIndex((track) => track.id === clip.trackId);
  if (trackIndex < 0) return video;
  const mates = video.clips
    .filter((item) => item.trackId === clip.trackId && item.id !== clip.id && item.kind !== 'audio')
    .slice()
    .sort((a, b) => (a.z ?? 0) - (b.z ?? 0) || a.startMs - b.startMs);
  const z = clip.z ?? 0;
  if (direction === 'forward') {
    const above = mates.find((item) => (item.z ?? 0) > z);
    if (above) return patchClip(video, clip.id, { z: (above.z ?? 0) + 1 });
    const higher = tracks[trackIndex + 1];
    if (higher) return patchClip(video, clip.id, { trackId: higher.id, z: 0 });
    return patchClip(video, clip.id, { z: z + 1 });
  }
  const below = [...mates].reverse().find((item) => (item.z ?? 0) < z);
  if (below) return patchClip(video, clip.id, { z: (below.z ?? 0) - 1 });
  const lower = tracks[trackIndex - 1];
  if (lower) return patchClip(video, clip.id, { trackId: lower.id, z: 0 });
  return patchClip(video, clip.id, { z: z - 1 });
}

export function patchClip(video: VideoProject, id: string, patch: Partial<TimelineClip>): VideoProject {
  if (!isClipProject(video)) return video;
  let changed = false;
  const clips = video.clips.map((clip) => {
    if (clip.id !== id) return clip;
    changed = true;
    return { ...clip, ...patch, id: clip.id, kind: clip.kind };
  });
  if (!changed) return video;
  return commit(video, clips);
}

/** Page drawn under a story caption, matched by beat order. */
export function storyCaptionPageId(video: VideoProject, clipId: string): string | null {
  if (!isClipProject(video)) return null;
  const captions = video.clips.filter((clip) => clip.groupId === 'story' && clip.kind === 'caption');
  const index = captions.findIndex((clip) => clip.id === clipId);
  if (index < 0) return null;
  const scenes = video.clips.filter((clip) => clip.groupId === 'story' && clip.kind === 'scene');
  return scenes[index]?.scene?.pageId ?? null;
}

/** The clip on the same track that ends into this one, if any. */
export function previousOnTrack(clips: readonly TimelineClip[], clip: TimelineClip): TimelineClip | null {
  let best: TimelineClip | null = null;
  for (const other of clips) {
    if (other.id === clip.id || other.trackId !== clip.trackId || other.kind === 'audio') continue;
    if (other.startMs >= clip.startMs) continue;
    if (!best || other.startMs > best.startMs) best = other;
  }
  return best;
}

/**
 * Why a transition cannot play on this clip, or null when it can.
 * A blend needs a clip before this one on the same track.
 */
export function transitionBlockReason(video: VideoProject, id: string): string | null {
  if (!isClipProject(video)) return 'Open a movie timeline before adding a transition.';
  const clip = video.clips.find((item) => item.id === id);
  if (!clip) return 'Select a clip, then choose a transition.';
  if (clip.kind === 'audio') return 'Transitions apply to picture and text, not audio.';
  if (!previousOnTrack(video.clips, clip)) {
    return 'Put another clip before this one on the same track, then choose a transition.';
  }
  return null;
}

/**
 * Store the transition and overlap this clip with the one before it so the
 * blend is actually on screen. A hard cut only clears the blend.
 */
export function applyClipTransition(video: VideoProject, id: string, transition: SceneTransition): VideoProject {
  if (!isClipProject(video)) return video;
  const clip = video.clips.find((item) => item.id === id);
  if (!clip || clip.kind === 'audio') return video;
  if (transition.id === 'none' || transition.id === 'cut') {
    return patchClip(video, id, { transitionIn: { id: 'cut', durationMs: 0 } });
  }
  const prev = previousOnTrack(video.clips, clip);
  if (!prev) return video;
  const dur = Math.round(
    Math.min(transition.durationMs || 500, VIDEO_MAX_TRANSITION_MS, prev.durationMs / 2, clip.durationMs / 2)
  );
  if (dur < 50) return video;
  const overlap = Math.max(0, Math.min(clipEnd(prev), clipEnd(clip)) - Math.max(prev.startMs, clip.startMs));
  let startMs = clip.startMs;
  if (overlap < dur) {
    startMs = Math.max(prev.startMs + VIDEO_MIN_CLIP_MS, clipEnd(prev) - dur);
  }
  return commit(
    video,
    video.clips.map((item) =>
      item.id === id
        ? { ...item, startMs, transitionIn: { ...transition, durationMs: dur } }
        : item
    )
  );
}

export function setClipTransition(video: VideoProject, id: string, transition: SceneTransition): VideoProject {
  return applyClipTransition(video, id, transition);
}

/** Playhead time in the middle of a clip's incoming blend, once one is stored. */
export function transitionHoldMs(video: VideoProject, id: string): number | null {
  if (!isClipProject(video)) return null;
  const clip = video.clips.find((item) => item.id === id);
  const durationMs = clip?.transitionIn?.durationMs ?? 0;
  if (!clip?.transitionIn || durationMs < 50 || clip.transitionIn.id === 'cut' || clip.transitionIn.id === 'none') {
    return null;
  }
  return clip.startMs + durationMs / 2;
}

export function withCanvas(video: VideoProject, canvas: VideoCanvas): VideoProject {
  if (!isClipProject(video)) return video;
  return {
    ...video,
    canvas,
    exportSettings: {
      ...video.exportSettings,
      width: canvas.width,
      height: canvas.height,
    },
  };
}

export function withBrand(video: VideoProject, brand: VideoBrand): VideoProject {
  if (!isClipProject(video)) return video;
  return { ...video, brand };
}

/**
 * Story beats become a scene clip plus a caption clip. Other lanes (music,
 * imported video, titles) stay where they are.
 */
export function applyStoryBeats(
  video: VideoProject,
  pages: Array<{ pageId: string; name: string }>,
  beatMs = 4000
): VideoProject {
  if (!isClipProject(video)) return video;
  const pageIds = new Set(pages.map((page) => page.pageId));
  const kept = video.clips.filter((clip) => {
    if (clip.groupId === 'story') return false;
    if (clip.kind === 'scene' && clip.scene && pageIds.has(clip.scene.pageId)) return false;
    return true;
  });
  const added: TimelineClip[] = [];
  pages.forEach((page, index) => {
    const startMs = index * beatMs;
    const name = page.name.trim() || `Scene ${index + 1}`;
    added.push({
      id: nextClipId('scene'),
      trackId: TRACK.visual,
      startMs,
      durationMs: beatMs,
      name,
      groupId: 'story',
      kind: 'scene',
      scene: { pageId: page.pageId },
    });
    added.push({
      id: nextClipId('cap'),
      trackId: TRACK.caption,
      startMs,
      durationMs: beatMs,
      name,
      groupId: 'story',
      kind: 'caption',
      caption: { text: name, source: 'manual' },
    });
  });
  const room = VIDEO_MAX_CLIPS - kept.length;
  return commit(video, [...kept, ...added.slice(0, Math.max(0, room))]);
}

/** Music and voice clips as the single-track shape the mixer already schedules. */
export function audioClipsToTracks(video: VideoProject): AudioTrack[] {
  if (!isClipProject(video)) return [];
  const muted = new Set(video.tracks.filter((track) => track.muted).map((track) => track.id));
  const tracks: AudioTrack[] = [];
  for (const clip of video.clips) {
    if (clip.kind !== 'audio' || !clip.audio || muted.has(clip.trackId)) continue;
    const audio = clip.audio;
    tracks.push({
      v: 1,
      trackId: clip.id,
      name: clip.name,
      source: audio.source,
      storagePath: audio.storagePath,
      url: audio.url,
      idbKey: audio.idbKey,
      mimeType: audio.mimeType,
      durationMs: audio.durationMs,
      offsetMs: clip.startMs,
      trimStartMs: audio.trimStartMs,
      trimEndMs: Math.min(audio.trimEndMs, audio.trimStartMs + clip.durationMs),
      volume: audio.volume,
      fade: audio.fade,
    });
  }
  return tracks;
}

/** Every timeline sound the export preview should play: music, voice, and camera audio. */
export function previewAudioTracks(video: VideoProject): AudioTrack[] {
  if (!isClipProject(video)) return video.audio ? [video.audio] : [];
  const voices: AudioTrack[] = [];
  for (const placed of placedVideoClips(video)) {
    const { clip, startMs, endMs } = placed;
    if (clip.muted || !(clip.volume > 0)) continue;
    const span = Math.max(0, endMs - startMs);
    voices.push({
      v: 1,
      trackId: `video-audio:${clip.clipId}:${startMs}`,
      name: clip.clipId,
      source: clip.source,
      storagePath: clip.storagePath,
      url: clip.url,
      idbKey: clip.idbKey,
      mimeType: clip.mimeType,
      durationMs: clip.durationMs,
      offsetMs: startMs,
      trimStartMs: clip.trimStartMs,
      trimEndMs: Math.min(clip.trimEndMs, clip.trimStartMs + span),
      volume: clip.volume,
      fade: { inMs: 0, outMs: 0 },
    });
  }
  return [...audioClipsToTracks(video), ...voices];
}

/** Timeline video clips (not Fabric layers inside a page) for the audio mix. */
export function placedVideoClips(video: VideoProject): Array<{
  clip: NonNullable<TimelineClip['video']>;
  startMs: number;
  endMs: number;
}> {
  if (!isClipProject(video)) return [];
  const muted = new Set(video.tracks.filter((track) => track.muted).map((track) => track.id));
  const out: Array<{ clip: NonNullable<TimelineClip['video']>; startMs: number; endMs: number }> = [];
  for (const clip of video.clips) {
    if (clip.kind !== 'video' || !clip.video || muted.has(clip.trackId)) continue;
    out.push({ clip: clip.video, startMs: clip.startMs, endMs: clipEnd(clip) });
  }
  return out;
}

/** Scene clips in timeline order, for callers that still speak pages. */
export function scenePageOrder(video: VideoProject, pages: ProjectPage[]): string[] {
  if (!isClipProject(video)) return pages.map((page) => page.pageId);
  return video.clips
    .filter((clip) => clip.kind === 'scene' && clip.scene)
    .slice()
    .sort((a, b) => a.startMs - b.startMs)
    .map((clip) => clip.scene?.pageId || '')
    .filter(Boolean);
}
